import { EntryLink } from '@/components/entry-link';
import ComparisonTable from '@/components/landingpage/comparison-table';
import Contact from '@/components/landingpage/contact';
import Features from '@/components/landingpage/features';
import Footer from '@/components/landingpage/footer';
import Header from '@/components/landingpage/header';
import HowItWorks from '@/components/landingpage/how-it-works';
import PressBar from '@/components/landingpage/press-bar';
import Secure from '@/components/landingpage/secure';
import TestimonialsSlider from '@/components/landingpage/testimonials-slider';
import TrustBar, { type TrustPartnerLogo } from '@/components/landingpage/trustbar';
import Faq from '@/components/marketing-pages/faq';
import { useLocalizedRoute } from '@/hooks/use-localized-route';
import { formatInterestRate } from '@/lib/utils';
import { type SharedData } from '@/types';
import { useAsset } from '@/utils/asset';
import { renderPageSeoElements } from '@/utils/page-seo';
import {
    buildPageStructuredData,
    buildSubpageBreadcrumbs,
    generateFaqStructuredData,
    generateFinancialProductStructuredData,
    getCanonicalUrl,
    type FaqItem,
} from '@/utils/seo-helpers';
import { Head, usePage } from '@inertiajs/react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    interestRate: number;
    accentColor: string;
    content: Record<string, Record<string, Record<string, string>>>;
    partnerName: string;
    logoUrl: string | null;
    logoDarkUrl: string | null;
}

/**
 * Generische, parametrisierte Partner-Landingpage (PF-11873).
 *
 * Strukturell identisch zu den hartcodierten haus-und-grund*.tsx: Banner und
 * Hero sind co-gebranded, alles darunter sind die Standardkomponenten.
 *
 * Die Akzentfarbe wird nicht über eigene Klassen gesetzt, sondern über die
 * CSS-Variablen --hug-blue / --hug-blue-dark. Tailwind v4 mappt in app.css
 * --color-hug-blue auf var(--hug-blue), und .hug-theme leitet daraus --hs-blue,
 * --hs-green, --primary und --ring ab. Dadurch übernimmt der komplette Teilbaum
 * die Partnerfarbe, ohne dass eine einzige Utility-Klasse angefasst werden muss.
 */
export default function PartnerLanding({ interestRate, accentColor, content, partnerName, logoUrl, logoDarkUrl }: Props) {
    const { t } = useTranslation();
    const route = useLocalizedRoute();
    const asset = useAsset();
    const { locale, currentPath } = usePage<SharedData>().props;

    const text = (path: string): string => {
        const [section, key] = path.split('.');

        return content[locale]?.[section]?.[key] ?? '';
    };

    const title = text('meta.title');
    const description = text('meta.description');
    const heroDescription = text('hero.description');
    const canonicalUrl = getCanonicalUrl(currentPath, locale);

    // Der dunklere Ton des CTA-Gradienten wird abgeleitet statt gepflegt —
    // ein zweites Farbfeld im Backoffice wäre Pflegeaufwand ohne Mehrwert.
    const accentStyle = {
        '--hug-blue': accentColor,
        '--hug-blue-dark': `color-mix(in srgb, ${accentColor} 88%, black)`,
    } as CSSProperties;

    const faqs = t('home.faq.items', {
        returnObjects: true,
        interestRate: formatInterestRate(interestRate, locale),
    }) as FaqItem[];

    const partnerLogos: TrustPartnerLogo[] = [
        { alt: t('home.trustbar.partners.logos.volksbank'), src: '/assets/logos/volksbank.svg' },
        { alt: t('home.trustbar.partners.logos.aareal'), src: '/assets/logos/aareal.svg', darkSrc: '/assets/logos/aareal-white.svg' },
        ...(logoUrl ? [{ alt: partnerName, src: logoUrl, darkSrc: logoDarkUrl ?? undefined, className: 'max-w-48', heightClass: 'h-20' }] : []),
    ];

    return (
        <>
            <Head title={title}>
                {renderPageSeoElements({
                    title,
                    description,
                    locale,
                    currentPath,
                    robots: 'noindex,follow',
                    structuredData: buildPageStructuredData({
                        locale,
                        breadcrumbs: buildSubpageBreadcrumbs(title, locale, currentPath),
                        blocks: [generateFinancialProductStructuredData({ title, description, url: canonicalUrl }), generateFaqStructuredData(faqs)],
                    }),
                })}
            </Head>

            <div className="bg-hug-blue text-white" style={accentStyle}>
                <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-1 px-6 py-2 text-center text-sm font-medium sm:flex-row sm:gap-4 lg:px-8">
                    <span>{text('banner.text')}</span>
                    <span className="hidden text-white/60 sm:inline" aria-hidden="true">
                        ·
                    </span>
                    <span className="text-white/90 italic">{text('banner.tagline')}</span>
                </div>
            </div>

            <Header />

            <main className="hug-theme" style={accentStyle}>
                <section className="relative isolate overflow-hidden bg-linear-to-b from-hug-blue/5 via-white to-white text-center xl:pb-16 xl:text-left dark:from-hug-blue/10 dark:via-gray-950 dark:to-gray-900">
                    <div
                        className="pointer-events-none absolute -top-40 left-1/2 -z-10 h-96 w-96 -translate-x-1/2 rounded-full bg-hug-blue/10 blur-3xl"
                        aria-hidden="true"
                    />
                    <div className="relative">
                        <div className="mx-auto max-w-7xl">
                            <div className="grid grid-cols-1 xl:grid-cols-2">
                                <div className="relative z-10 flex flex-col px-6 py-12 sm:py-16 xl:px-8 xl:py-32">
                                    <div className="mx-auto inline-flex w-fit items-center gap-2 rounded-full border border-hug-blue/20 bg-hug-blue/10 px-4 py-1.5 text-sm font-semibold text-hug-blue xl:mx-0 dark:border-hug-blue/30 dark:bg-hug-blue/20">
                                        <span className="size-2 rounded-full bg-hug-blue" aria-hidden="true" />
                                        {text('hero.badge')}
                                    </div>

                                    <h1 className="mt-6 text-5xl font-bold tracking-tight text-balance text-headline sm:text-6xl">
                                        {text('hero.title')}
                                    </h1>

                                    <p className="mt-6 text-2xl font-medium text-paragraph sm:text-3xl">{text('hero.subtitle')}</p>

                                    {heroDescription && <p className="mt-6 text-lg leading-relaxed text-paragraph">{heroDescription}</p>}

                                    <div className="mt-10">
                                        <EntryLink
                                            href={route('register')}
                                            className="group relative inline-flex cursor-pointer overflow-hidden rounded-lg bg-linear-to-r from-hug-blue to-hug-blue-dark px-8 py-4 text-lg font-bold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                                        >
                                            <span className="absolute inset-0 -translate-x-full rotate-45 bg-white opacity-30 blur-sm transition-transform duration-500 group-hover:translate-x-full" />
                                            <span className="relative z-10">{t('home.hero.ctaPrimary')}</span>
                                        </EntryLink>
                                    </div>

                                    <div className="mt-10 flex flex-col items-center xl:mt-16 xl:items-start">
                                        <p className="text-base font-semibold text-paragraph">{text('hero.partner')}</p>
                                        <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-center">
                                            <img
                                                src={asset('logos/VVRBLogo.png')}
                                                alt="Vereinigte Volksbank Raiffeisenbank eG"
                                                className="h-10 w-auto opacity-90 dark:hidden"
                                                loading="lazy"
                                            />
                                            <img
                                                src={asset('logos/VVRBLogoWeiss.png')}
                                                alt="Vereinigte Volksbank Raiffeisenbank eG"
                                                className="hidden h-10 w-auto opacity-90 dark:block"
                                                loading="lazy"
                                            />
                                            {logoUrl && (
                                                <>
                                                    <span className="hidden text-gray-300 sm:block dark:text-gray-600">|</span>
                                                    <img
                                                        src={logoUrl}
                                                        alt={partnerName}
                                                        className={logoDarkUrl ? 'h-16 w-auto opacity-95 dark:hidden' : 'h-16 w-auto opacity-95'}
                                                        loading="lazy"
                                                    />
                                                    {logoDarkUrl && (
                                                        <img
                                                            src={logoDarkUrl}
                                                            alt={partnerName}
                                                            className="hidden h-16 w-auto opacity-95 dark:block"
                                                            loading="lazy"
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="relative block p-4 pb-12 sm:pb-16 xl:mt-12 xl:flex xl:p-0 xl:pb-0">
                                    <div className="xl:absolute xl:inset-y-0 xl:right-0 xl:left-0 xl:min-h-150 xl:w-[150vw]">
                                        <img
                                            src={asset('images/hero/dashboard.avif')}
                                            alt="heyKaution Dashboard"
                                            className="h-full w-full rounded-2xl object-contain object-left shadow-2xl dark:hidden"
                                        />
                                        <img
                                            src={asset('images/hero/dashboard-dark.avif')}
                                            alt="heyKaution Dashboard"
                                            className="hidden h-full w-full rounded-2xl object-contain object-left shadow-2xl dark:block"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <PressBar />

                <Secure customDescription1={text('secure.description1')} customDescription2={text('secure.description2')} />

                <Features />
                <HowItWorks />
                <ComparisonTable />
                <TrustBar partnerLogos={partnerLogos} />
                <TestimonialsSlider />

                <Faq faqs={faqs} title={t('home.faq.title')} />

                <Contact />
            </main>

            <Footer />
        </>
    );
}
